import React from "react";
import { motion, HTMLMotionProps } from "framer-motion";

interface ButtonProps extends Omit<HTMLMotionProps<"button">, "disabled"> {
  variant?: "primary" | "secondary" | "danger" | "recording";
  size?: "sm" | "md" | "lg" | "xl";
  isLoading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  isLoading = false,
  fullWidth = false,
  children,
  className = "",
  disabled,
  ...props
}) => {
  const baseStyles =
    "font-medium transition-all rounded-xl flex items-center justify-center gap-2";

  const variants = {
    primary:
      "bg-gradient-to-r from-[#0071E3] to-[#40A9FF] text-white hover:from-[#0077ED] hover:to-[#40A9FF] shadow-lg shadow-blue-500/20",
    secondary:
      "bg-white text-[#1D1D1F] hover:bg-gray-50 border border-gray-200 shadow-sm",
    danger:
      "bg-gradient-to-r from-[#FF3B30] to-[#FF453A] text-white hover:from-[#FF453A] hover:to-[#FF453A] shadow-lg shadow-red-500/20",
    recording:
      "bg-gradient-to-r from-[#0071E3] to-[#40A9FF] text-white hover:from-[#0077ED] hover:to-[#40A9FF] shadow-lg shadow-blue-500/20 !text-4xl !py-6 !px-8 mx-auto font-semibold tracking-tight",
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-6 py-4 text-lg",
    xl: "px-8 py-6 text-4xl",
  };

  const widthClass = fullWidth ? "w-full" : "";
  const disabledClass = disabled
    ? "opacity-50 cursor-not-allowed"
    : "active:scale-[0.98]";

  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.01 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${disabledClass} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        children
      )}
    </motion.button>
  );
};

export default Button;
